import { cn } from "@/lib/utils";
import type { PeriodType } from "@/apis/compare/route";
import {
  buildValueCellDataset,
  formatCellForDisplay,
  formatExpectedAutoSumValueForDisplay,
} from "@/utils/ixd-editor/cell-display";
import { isNumericSchemaKind } from "@/utils/ixd-editor/value-validation";
import { getLineItemUnitForCell } from "@/utils/ixd-editor/unit-conversion";
import {
  cellChangeKey,
  identityChangeKey,
  isValueCell,
  type CellChangeOrigin,
} from "@/utils/ixd-cell-model";
import TableCell, { type TableCellColumn } from "../table-cell";
import EditableTableCell from "../editable-table-cell";
import { getCellIdentityLookupInfo } from "./cell-lookup";
import { getRelatedIdentityCount } from "./chunk-change-detection";
import { useIxdPreviewConfig } from "./ixd-preview-config-context";
import { useDlViewConfig } from "./dl-view-context";

export interface SemanticContext {
  negated: boolean;
  decimalDigits: number | null;
}

interface TableBodyCellProps {
  column: TableCellColumn;
  periodType: PeriodType;
  rowIndex: number;
  colIndex: number;
  semanticContext: SemanticContext;
  cellChanges?: Record<string, string>;
  changeOrigins?: Record<string, CellChangeOrigin>;
  cellIdentityByNormalizedCellId?: Map<string, string>;
  identityChanges?: Map<string, string>;
  onCellEdit?: (
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
}

export const TableBodyCell = ({
  column,
  periodType,
  rowIndex,
  colIndex,
  semanticContext,
  cellChanges,
  changeOrigins,
  cellIdentityByNormalizedCellId,
  identityChanges,
  onCellEdit,
}: TableBodyCellProps) => {
  const {
    sectionId,
    identitySectionId,
    dlViewIdentityByNormalizedCellId,
    schemaTypeByNormalizedCellId,
    referenceBalance,
    labelMap,
    lineItemUnits,
    unitDivisor,
  } = useDlViewConfig();

  const cellCurrencyUnit = column.id
    ? getLineItemUnitForCell(lineItemUnits, column.id)?.unit.unit
    : undefined;
  const effectiveUnitDivisor =
    cellCurrencyUnit !== undefined ? 10 ** cellCurrencyUnit : unitDivisor;
  const {
    editable,
    cellNotices,
    identityLocationsByKey,
    showRawValueInEdit,
    onCellEditStart,
    onCellMouseDown,
    onCellMouseEnter,
    onCellMouseUp,
  } = useIxdPreviewConfig();

  const uid =
    column.id && sectionId
      ? `${sectionId}-${periodType}-r${rowIndex}-c${colIndex}`
      : undefined;
  const identityLocationUid =
    column.id && identitySectionId
      ? `${identitySectionId}-${periodType}-r${rowIndex}-c${colIndex}`
      : undefined;
  const { normalizedCellId, identityKey: cellIdentityKey } =
    getCellIdentityLookupInfo(
      periodType,
      column,
      dlViewIdentityByNormalizedCellId,
      cellIdentityByNormalizedCellId,
    );
  const effectiveSchemaType = normalizedCellId
    ? (schemaTypeByNormalizedCellId.get(normalizedCellId) ?? column.schemaType)
    : column.schemaType;
  const effectiveCellId = column.id;
  // store 키는 ChangeKey(cell:/identity: prefix)라 raw cellId 로 조회하면 항상
  // undefined 다. cellId fallback 경로도 cellChangeKey 로 prefix 를 붙여 조회한다.
  const directChangeKey = (() => {
    if (!cellChanges || !column.id) return undefined;
    const sourceKey = cellChangeKey(column.id);
    if (cellChanges[sourceKey] !== undefined) return sourceKey;
    if (!normalizedCellId) return undefined;
    const normalizedKey = cellChangeKey(normalizedCellId);
    return cellChanges[normalizedKey] !== undefined ? normalizedKey : undefined;
  })();
  const directPendingValue = directChangeKey
    ? cellChanges?.[directChangeKey]
    : undefined;
  const identityPendingValue = cellIdentityKey
    ? identityChanges?.get(cellIdentityKey)
    : undefined;
  const relatedIdentityCount = getRelatedIdentityCount(
    cellIdentityKey,
    identityLocationsByKey,
  );
  // identity 를 아는 셀은 identity 로만 판정한다. cellId fallback 을 허용하면 축만
  // 다른 표의 셀이 같은 cellId 를 갖고 있어 남의 편집값을 그대로 표시한다.
  const pendingValue = cellIdentityKey
    ? identityPendingValue
    : directPendingValue;
  const pendingChangeKey = cellIdentityKey
    ? identityChangeKey(cellIdentityKey)
    : directChangeKey;
  const changeOrigin =
    pendingValue !== undefined && pendingChangeKey !== undefined
      ? changeOrigins?.[pendingChangeKey]
      : undefined;
  const showManualEditMarker = changeOrigin === "manual";

  // negated는 라인아이템(concept) 단위 표시 속성이다.
  if (editable) {
    return (
      <EditableTableCell
        column={column}
        periodType={periodType}
        uniqueId={uid}
        identityLocationUid={identityLocationUid}
        schemaType={effectiveSchemaType}
        referenceBalance={referenceBalance}
        labelMap={labelMap}
        editedValue={pendingValue}
        showManualEditMarker={showManualEditMarker}
        changeOrigin={changeOrigin}
        editCellId={effectiveCellId}
        cellIdentityKey={cellIdentityKey}
        relatedCellIdentityCount={relatedIdentityCount}
        onEdit={onCellEdit}
        onEditStart={onCellEditStart}
        unitDivisor={effectiveUnitDivisor}
        showRawValueInEdit={showRawValueInEdit}
        negated={semanticContext.negated}
        decimalDigits={semanticContext.decimalDigits}
        onCellMouseDown={onCellMouseDown}
        onCellMouseEnter={onCellMouseEnter}
        onCellMouseUp={onCellMouseUp}
      />
    );
  }

  if (!isValueCell(column)) {
    return (
      <TableCell
        column={column}
        periodType={periodType}
        uniqueId={uid}
        referenceBalance={referenceBalance}
        labelMap={labelMap}
      />
    );
  }

  const hasPendingChange = pendingValue !== undefined;
  const rawText = hasPendingChange ? pendingValue : column.text;
  const { displayText, unitDivisor: appliedDivisor } = formatCellForDisplay({
    rawText,
    schemaType: effectiveSchemaType,
    unitDivisor: effectiveUnitDivisor,
    negated: semanticContext.negated,
    decimalDigits: semanticContext.decimalDigits,
  });
  const expectedAutoSumDisplayText = formatExpectedAutoSumValueForDisplay(
    column.expectedAutoSumValue,
  );
  const tdProps = buildValueCellDataset({
    cellId: effectiveCellId,
    sourceCellId: column.id,
    rawText,
    displayText,
    identityKey: cellIdentityKey,
    schemaType: effectiveSchemaType,
    unitDivisor: appliedDivisor,
    negated: semanticContext.negated,
    decimalDigits: semanticContext.decimalDigits,
    readonly: !column.editable,
    edited: hasPendingChange,
    axisTotal: column.isAxisTotal,
  }) satisfies React.TdHTMLAttributes<HTMLTableCellElement>;
  // 값이 들어가지 않은 이유는 그 셀에서 바로 보이는 것이 가장 직관적이다.
  const cellNotice = cellIdentityKey
    ? cellNotices?.get(cellIdentityKey)
    : undefined;
  const selectableTdProps = {
    ...tdProps,
    "data-cell-identity-location-uid": identityLocationUid,
    ...(cellNotice
      ? {
          "data-cell-notice": cellNotice.tone,
          title: cellNotice.description,
        }
      : {}),
    onMouseDown:
      uid && onCellMouseDown
        ? (event: React.MouseEvent) => onCellMouseDown(uid, event)
        : undefined,
    onMouseEnter: onCellMouseEnter,
    onMouseUp: onCellMouseUp,
  } satisfies React.TdHTMLAttributes<HTMLTableCellElement> &
    Record<string, unknown>;

  return (
    <TableCell
      column={column}
      periodType={periodType}
      uniqueId={uid}
      referenceBalance={referenceBalance}
      labelMap={labelMap}
      displayText={displayText}
      expectedAutoSumDisplayText={expectedAutoSumDisplayText}
      relatedCellIdentityCount={relatedIdentityCount}
      showManualEditMarker={showManualEditMarker}
      showNoticeMarker={cellNotice?.tone === "blocked"}
      alignRight={isNumericSchemaKind(effectiveSchemaType)}
      cellClassName={cn(
        hasPendingChange && "bg-amber-50",
        onCellMouseDown && "cursor-cell select-none",
        // 확인 알림 배경은 편집 대기(amber)보다 우선한다.
        cellNotice?.tone === "conflict" && "bg-purple-100 hover:bg-purple-200",
      )}
      tdProps={selectableTdProps}
    />
  );
};
